/**
 * Whale System - hidden per-meme-coin state machine:
 * dormant -> accumulating -> pumping -> dumping -> dormant.
 * Phase transitions only; price influence is applied by exchangeMarketSystem
 * via whaleBias(). Not surfaced in UI except the ad-gated tracker reveal.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import type { CoinConfig } from '../config/coins.js';
import { WHALE_CONFIG } from '../config/whale.js';
import type { WhalePhase, WhaleState } from '../infoState.js';

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

const NEXT_PHASE: Record<WhalePhase, WhalePhase> = {
  dormant: 'accumulating',
  accumulating: 'pumping',
  pumping: 'dumping',
  dumping: 'dormant',
};

function phaseDuration(phase: WhalePhase): number {
  switch (phase) {
    case 'dormant': return randRange(WHALE_CONFIG.dormantMin, WHALE_CONFIG.dormantMax);
    case 'accumulating': return randRange(WHALE_CONFIG.accumMin, WHALE_CONFIG.accumMax);
    case 'pumping': return randRange(WHALE_CONFIG.pumpMin, WHALE_CONFIG.pumpMax);
    case 'dumping': return randRange(WHALE_CONFIG.dumpMin, WHALE_CONFIG.dumpMax);
  }
}

/** Advance a whale to its next phase (pure - exported for tests). */
export function advanceWhale(whale: WhaleState, coinPrice: number, now: number): WhaleState {
  const next = NEXT_PHASE[whale.phase];
  return {
    ...whale,
    phase: next,
    phaseEndsAt: now + phaseDuration(next),
    accumStartPrice: next === 'accumulating' ? coinPrice : whale.accumStartPrice,
    // Rug pull trigger source: mark the moment a pump ends (Sprint 2b)
    pumpEndedAt: whale.phase === 'pumping' ? now : whale.pumpEndedAt,
  };
}

/** True when any whale other than exceptId is pumping or dumping (market chaos guard). */
export function hasActivePumper(whales: Record<string, WhaleState>, exceptId: string): boolean {
  return Object.entries(whales).some(
    ([id, w]) => id !== exceptId && (w.phase === 'pumping' || w.phase === 'dumping')
  );
}

/**
 * Momentum bias contributed by the whale, in price-fraction per tick.
 * Dumping pulls harder while price is still above the accumulation target.
 */
export function whaleBias(whale: WhaleState, price: number, cfg: CoinConfig): number {
  switch (whale.phase) {
    case 'accumulating':
      return WHALE_CONFIG.accumBias * cfg.volatility;
    case 'pumping':
      return WHALE_CONFIG.pumpBias * cfg.volatility;
    case 'dumping': {
      let bias = -WHALE_CONFIG.dumpBias * cfg.volatility;
      if (whale.accumStartPrice > 0 && price > whale.accumStartPrice * WHALE_CONFIG.dumpTargetRatio) {
        bias -= WHALE_CONFIG.dumpExtraAboveTarget * cfg.volatility;
      }
      return bias;
    }
    default:
      return 0;
  }
}

export const whaleSystem: TickSystem<GameState, GameAction> = {
  name: 'whale',

  update(state) {
    if (!state.isRunning || !state.whales || !state.exchange) return state;
    const now = Date.now();

    let changed = false;
    const whales: Record<string, WhaleState> = { ...state.whales };
    for (const [coinId, whale] of Object.entries(state.whales)) {
      // Only listed coins run whale cycles; delisted / pool coins stay dormant.
      if (state.exchange.listing[coinId]?.status !== 'listed') continue;
      if (now < whale.phaseEndsAt) continue;
      // Only one coin may be pumping/dumping at once: blocked whales keep
      // accumulating and retry shortly (prevents a chaotic market).
      if (whale.phase === 'accumulating' && hasActivePumper(whales, coinId)) {
        const hold = WHALE_CONFIG.pumpHoldMin
          + Math.random() * (WHALE_CONFIG.pumpHoldMax - WHALE_CONFIG.pumpHoldMin);
        whales[coinId] = { ...whale, phaseEndsAt: now + hold };
      } else {
        const price = state.exchange.coins[coinId]?.price ?? whale.accumStartPrice;
        whales[coinId] = advanceWhale(whale, price, now);
      }
      changed = true;
    }

    return changed ? { ...state, whales } : state;
  },
};
